import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  muscleKey,
  weeklyTargetDegradedMax,
  weeklyTargetRowLabel,
  type CatalogMuscleSlug,
  type UnitsPreference,
  type WeeklyTargetRow,
} from '@heraion/shared';
import { TargetMeterRow } from '../index';

import type { useTheme } from './theme';

// AP-3b: the in-workout contextual meter. The weekly target for the muscles
// the active exercise trains, with THIS session's just-logged sets already
// folded in (useInWorkoutTargets → computeInWorkoutTargets). Same grammar as
// the Home three-zone card, because it renders the same <TargetMeterRow>.
//
// #308: only the rows. The fold is the target icon on the card's row 1
// (Antenore: «solo icona»), and the card renders this when it is open. The
// host passes nothing at all when there are no rows, so the icon goes too.

/** The card's `targetStrip` slot: the rows, or nothing at all when there are
 *  none, so the card draws no target icon. */
export function focusTargetStrip(props: Props) {
  return props.rows.length > 0 ? <FocusPanelTargetStrip {...props} /> : undefined;
}

type Props = {
  rows: WeeklyTargetRow[];
  units: UnitsPreference;
  locale: string;
  // Resolve an exercise slug → display name for exercise-axis rows. Muscle rows
  // resolve via muscleKey() here.
  resolveExerciseName: (slug: string) => string;
  theme: ReturnType<typeof useTheme>;
};

export function FocusPanelTargetStrip({
  rows,
  units,
  locale,
  resolveExerciseName,
  theme,
}: Props) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  if (rows.length === 0) return null;

  // The shared helpers keep the label and the neutral-bar scale identical to
  // Home's card and the coach's panel; only the muscle-name resolver is local.
  const labelFor = (row: WeeklyTargetRow): string =>
    weeklyTargetRowLabel(row, resolveExerciseName, (slug) =>
      t(muscleKey(slug as CatalogMuscleSlug)),
    );

  // Untargeted (neutral) rows share the busiest row's scale, matching the card.
  const degradedMax = weeklyTargetDegradedMax(rows);

  return (
    <View style={styles.box}>
      {rows.map((row) => (
        <View key={`${row.axis}:${row.key}:${row.metric}`} style={styles.row}>
          <TargetMeterRow
            theme={theme}
            row={row}
            label={labelFor(row)}
            units={units}
            locale={locale}
            degradedMax={degradedMax}
          />
        </View>
      ))}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r } = theme;
  return StyleSheet.create({
    // Same surface + r3 as the Home meter card: surface, not surface2, keeps
    // the zone `complete` text at the card's contrast (DS-13 watch).
    box: {
      backgroundColor: p.surface,
      borderRadius: r.r3,
      borderWidth: 1,
      borderColor: p.border,
      paddingHorizontal: s.s3,
      paddingBottom: s.s2,
    },
    row: {
      paddingTop: s.s2,
      paddingBottom: s.s1,
    },
  });
}
